// CrmPage — lead Meta importati da Zernio, attribuiti al cliente e lavorati.
// I dati arrivano da /api/crm/*, un router autonomo che NON tocca /api/zernio/*
// (vedi backend/routes/crm.py).
//
// Impianto visivo: lista respirata a sinistra, dettaglio in colonna fissa a
// destra (nato dall'artboard 1a di Claude Design). Riallineato al design
// system dell'app (docs/11-design-system.md) — accento viola #5b5bd6,
// Plus Jakarta Sans/IBM Plex Mono — al posto della palette calda
// grafite/terracotta con cui era nato. Due conseguenze pratiche restano
// dall'impianto originale:
//   1. Il dettaglio non e' un dialog ma una colonna sempre presente: si scorre
//      la lista continuando a vedere il lead selezionato, che e' il gesto
//      naturale quando si telefona a una fila di contatti uno dopo l'altro.
//   2. Nome, email e telefono stanno in una sola cella su due righe. I moduli
//      Meta hanno campi diversi fra loro: con tre colonne fisse la tabella era
//      per meta' trattini e sembrava rotta. Quando un dato manca lo si dice a
//      parole, non con un trattino.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Search, FileDown, RefreshCw, ChevronRight, ChevronLeft, ChevronsLeft,
  Phone, MessageCircle, Mail, X, AlertTriangle, Inbox, Check, ChevronsUpDown, Users,
} from "lucide-react";
import { PeriodPicker, computePeriodDates } from "./social/common";
import { PRIMARY_BTN, SECONDARY_BTN, EmptyState } from "../components/designSystem";

const PAGE_SIZE = 50;

// Pulsante piccolo solo-icona (paginazione, nav popover): stesso linguaggio
// di SECONDARY_BTN ma quadrato — non componibile appendendo classi a
// SECONDARY_BTN perche' l'ordine con cui Tailwind genera il CSS non segue
// l'ordine di scrittura della className, quindi "px-0" in coda non è
// garantito vincere su "px-3".
const ICON_BTN =
  "w-8 h-8 rounded-[10px] border border-[#e0e0ea] bg-white flex items-center justify-center text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#e0e0ea] disabled:hover:text-[#565d6d]";

// Esiti allineati alla whitelist del backend (routes/crm.py::STATI). Le `key`
// sono contratto con l'API, non cambiano; dot/bg sono le uniche cinque
// combinazioni semantiche del design system che restano distinguibili fra
// loro: viola (nuovo), ambra (da richiamare), teal (contattato), verde
// (qualificato), neutro (non interessato).
const STATI = [
  { key: "nuovo", label: "Nuovo", dot: "#5b5bd6", bg: "#eeeefc" },
  { key: "da_richiamare", label: "Da richiamare", dot: "#f59e0b", bg: "#fef3c7" },
  { key: "contattato", label: "Contattato", dot: "#0d8377", bg: "#e4f6f3" },
  { key: "qualificato", label: "Qualificato", dot: "#12a05c", bg: "#e7f7ee" },
  { key: "non_interessato", label: "Non interessato", dot: "#9095a1", bg: "#f3f3f8" },
];
const STATO_BY_KEY = Object.fromEntries(STATI.map((s) => [s.key, s]));

const NAME_PATTERNS = ["full_name", "full name", "nome_e_cognome", "nome_completo", "nome"];
const EMAIL_PATTERNS = ["email", "mail"];
const PHONE_PATTERNS = ["phone", "telefono", "cellulare", "numero_di_telefono"];

function pickField(fields, patterns) {
  if (!fields) return "";
  const entries = Object.entries(fields);
  for (const pat of patterns) {
    const hit = entries.find(([k]) => k.toLowerCase().includes(pat));
    if (hit && hit[1] != null && String(hit[1]).trim()) return String(hit[1]);
  }
  return "";
}

function recapiti(lead) {
  const f = lead?.fields || {};
  return {
    nome: pickField(f, NAME_PATTERNS),
    email: pickField(f, EMAIL_PATTERNS),
    telefono: pickField(f, PHONE_PATTERNS),
  };
}

// Il miglior identificativo disponibile, con la mancanza dichiarata a parole.
function identificativo(lead) {
  const { nome, email, telefono } = recapiti(lead);
  const n = Object.keys(lead?.fields || {}).length;
  if (nome) return { titolo: nome, sotto: [email, telefono].filter(Boolean).join(" · ") || "Nessun recapito nel modulo", assente: false };
  if (email) return { titolo: email, sotto: telefono || "Nome non presente nel modulo", assente: false };
  if (telefono) return { titolo: telefono, sotto: "Solo telefono nel modulo", assente: false };
  return { titolo: "Senza identificativo", sotto: `${n} rispost${n === 1 ? "a" : "e"} nel dettaglio`, assente: true };
}

function iniziali(lead) {
  const { nome, email } = recapiti(lead);
  const base = nome || email || "?";
  const parti = base.replace(/[^\p{L}\s.]/gu, " ").trim().split(/[\s.]+/).filter(Boolean);
  return ((parti[0]?.[0] || "") + (parti[1]?.[0] || "")).toUpperCase() || "?";
}

// I numeri dei moduli arrivano in formati diversi: wa.me vuole solo cifre, e
// senza prefisso internazionale il link aprirebbe una chat sbagliata.
function numeroWhatsApp(telefono) {
  const cifre = (telefono || "").replace(/[^\d+]/g, "");
  if (!cifre) return "";
  if (cifre.startsWith("+")) return cifre.slice(1);
  if (cifre.startsWith("00")) return cifre.slice(2);
  if (cifre.startsWith("3") && cifre.length >= 9) return `39${cifre}`;
  return cifre;
}

function formatDateTime(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
  catch (_) { return iso; }
}

function splitDateTime(iso) {
  if (!iso) return { data: "-", ora: "" };
  try {
    const d = new Date(iso);
    return {
      data: d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }),
      ora: d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch (_) { return { data: iso, ora: "" }; }
}

function PillStato({ stato }) {
  const s = STATO_BY_KEY[stato] || STATO_BY_KEY.nuovo;
  return (
    <span
      className="inline-flex items-center gap-[7px] rounded-full px-2.5 py-1 text-[11px] font-medium text-[#15161c] whitespace-nowrap scheda-font-ui"
      style={{ background: s.bg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

function formatGiornoSparkline(isoGiorno) {
  if (!isoGiorno) return "";
  try {
    const d = new Date(isoGiorno + "T12:00:00");
    const oggi = new Date();
    const isOggi = d.toDateString() === oggi.toDateString();
    const ieri = new Date(oggi);
    ieri.setDate(ieri.getDate() - 1);
    const isIeri = d.toDateString() === ieri.toDateString();

    const dataFormatted = d.toLocaleDateString("it-IT", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

    if (isOggi) return `Oggi · ${dataFormatted}`;
    if (isIeri) return `Ieri · ${dataFormatted}`;
    return dataFormatted;
  } catch (_) {
    return isoGiorno;
  }
}

// Barre degli ultimi 14 giorni con hover interattivo e tooltip con dettagli completi.
function Sparkline({ serie }) {
  const max = Math.max(1, ...(serie || []).map((g) => g.count));
  const oggiIso = new Date().toISOString().slice(0, 10);

  return (
    <TooltipProvider delayDuration={50}>
      <div className="flex items-end gap-[4px] h-8 pt-0.5" data-testid="crm-sparkline">
        {(serie || []).map((g) => {
          const heightPx = Math.max(3, Math.round((g.count / max) * 30));
          const hasLeads = g.count > 0;
          const isOggi = g.giorno === oggiIso;
          return (
            <Tooltip key={g.giorno}>
              <TooltipTrigger asChild>
                <div
                  className="group flex flex-col items-center justify-end h-full px-[1px] cursor-pointer"
                  data-testid={`crm-sparkline-bar-${g.giorno}`}
                >
                  <div
                    className="w-2 rounded-t-[2px] transition-all duration-150 group-hover:scale-y-110 group-hover:opacity-100"
                    style={{
                      height: `${heightPx}px`,
                      background: hasLeads ? (isOggi ? "#5b5bd6" : "#c3c6d0") : "#dcdce8",
                    }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="center"
                sideOffset={6}
                className="p-3 shadow-xl border border-[#e9e9f1] rounded-[14px] min-w-[190px] text-left pointer-events-none z-50 bg-white scheda-font-ui"
              >
                <div className="text-[12px] font-semibold capitalize mb-1 text-[#15161c]">
                  {formatGiornoSparkline(g.giorno)}
                </div>

                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <span className="text-[11px] text-[#9095a1]">Lead totali:</span>
                  <span className={`scheda-font-mono font-bold text-[13px] ${hasLeads ? "text-[#5b5bd6]" : "text-[#9095a1]"}`}>
                    {g.count}
                  </span>
                </div>

                {hasLeads ? (
                  <div className="space-y-1 pt-1.5 border-t border-[#f0f0f6]">
                    {STATI.map((s) => {
                      const num = g.per_stato?.[s.key] || 0;
                      if (!num) return null;
                      return (
                        <div key={s.key} className="flex items-center justify-between text-[11px] gap-2">
                          <span className="flex items-center gap-1.5 text-[#565d6d]">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.dot }} />
                            {s.label}
                          </span>
                          <span className="scheda-font-mono font-medium text-[#15161c]">{num}</span>
                        </div>
                      );
                    })}

                    {(g.organici > 0 || g.sponsorizzati > 0) && (
                      <div className="pt-1.5 mt-1.5 border-t border-[#f0f0f6] flex items-center justify-between text-[10px] text-[#9095a1]">
                        <span>Meta Ads: <strong className="text-[#15161c]">{g.sponsorizzati ?? 0}</strong></span>
                        <span>Organici: <strong className="text-[#15161c]">{g.organici ?? 0}</strong></span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[11px] italic pt-1 border-t border-[#f0f0f6] text-[#9095a1]">
                    Nessun lead ricevuto
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function CrmClientPicker({ clients, value, onChange, countsByClient = {}, totalCount = 0, testid = "crm-client-picker" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedClient = clients.find((c) => c.client_id === value);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [clients, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`group flex items-center justify-between gap-2.5 h-10 px-3.5 rounded-[10px] text-[13px] font-medium scheda-font-ui transition-all duration-150 cursor-pointer bg-white hover:border-[#c9c9f0] focus:outline-none text-[#15161c] ${open ? "border border-[#5b5bd6]" : "border border-[#e4e4ed]"}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {selectedClient && selectedClient.client_id ? (
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-[#eeeefc] text-[#5b5bd6]">
                {(selectedClient.name || "?").slice(0, 1).toUpperCase()}
              </span>
            ) : (
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 bg-[#f3f3f8] text-[#9095a1]">
                <Users className="w-3 h-3" />
              </span>
            )}
            <span className="truncate max-w-[190px] text-[13.5px]">
              {selectedClient && selectedClient.client_id ? selectedClient.name : "Tutti i clienti"}
            </span>
          </div>
          <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 opacity-40 group-hover:opacity-80 transition-opacity ml-1 text-[#9095a1]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[300px] p-2 rounded-[14px] shadow-lg border border-[#e9e9f1] z-50 scheda-pop-anim scheda-font-ui bg-white text-[#15161c]"
      >
        {/* Search header */}
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-[10px] mb-2 bg-[#fbfbfd] border border-[#f0f0f6]">
          <Search className="w-3.5 h-3.5 shrink-0 text-[#9095a1]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca cliente…"
            className="w-full bg-transparent text-[12.5px] focus:outline-none placeholder:text-[#9095a1] text-[#15161c]"
            autoFocus
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-[#9095a1] hover:text-[#15161c] p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Client list */}
        <div className="max-h-[260px] overflow-y-auto space-y-0.5 pr-0.5">
          {/* Tutti i clienti option */}
          {(!search || "tutti i clienti".includes(search.toLowerCase())) && (
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
              className={`w-full flex items-center justify-between px-2.5 py-2 rounded-[10px] text-left text-[12.5px] transition-colors cursor-pointer hover:bg-[#f3f3f8] ${!value ? "bg-[#eeeefc] text-[#5b5bd6] font-semibold hover:bg-[#eeeefc]" : "text-[#15161c]"}`}
            >
              <div className="flex items-center gap-2.5 truncate">
                <Check className={`w-3.5 h-3.5 shrink-0 text-[#5b5bd6] ${!value ? "opacity-100" : "opacity-0"}`} />
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${!value ? "bg-[#c9c9f0] text-[#5b5bd6]" : "bg-[#f3f3f8] text-[#9095a1]"}`}>
                  <Users className="w-2.5 h-2.5" />
                </span>
                <span>Tutti i clienti</span>
              </div>
              {totalCount > 0 && (
                <span className={`text-[10.5px] scheda-font-mono px-1.5 py-0.5 rounded-full ${!value ? "bg-[#c9c9f0] text-[#5b5bd6]" : "bg-[#f3f3f8] text-[#9095a1]"}`}>
                  {totalCount}
                </span>
              )}
            </button>
          )}

          {filtered.filter((c) => c.client_id).map((c) => {
            const isSelected = value === c.client_id;
            const count = countsByClient[c.client_id];
            const initial = (c.name || "?").slice(0, 1).toUpperCase();
            return (
              <button
                key={c.client_id}
                type="button"
                onClick={() => { onChange(c.client_id); setOpen(false); setSearch(""); }}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-[10px] text-left text-[12.5px] transition-colors cursor-pointer ${isSelected ? "bg-[#eeeefc] text-[#5b5bd6] font-semibold" : "text-[#15161c] hover:bg-[#f3f3f8]"}`}
              >
                <div className="flex items-center gap-2.5 truncate min-w-0">
                  <Check className={`w-3.5 h-3.5 shrink-0 text-[#5b5bd6] ${isSelected ? "opacity-100" : "opacity-0"}`} />
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isSelected ? "bg-[#c9c9f0] text-[#5b5bd6]" : "bg-[#f3f3f8] text-[#565d6d]"}`}>
                    {initial}
                  </span>
                  <span className="truncate">{c.name}</span>
                </div>
                {count != null && count > 0 && (
                  <span className={`text-[10.5px] scheda-font-mono px-1.5 py-0.5 rounded-full shrink-0 ml-1.5 ${isSelected ? "bg-[#c9c9f0] text-[#5b5bd6]" : "bg-[#f3f3f8] text-[#9095a1]"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}

          {filtered.filter((c) => c.client_id).length === 0 && search && (
            <div className="py-6 text-center text-xs text-[#9095a1]">
              Nessun cliente trovato per "{search}"
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Metrica({ label, valore, contorno, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#9095a1]">{label}</div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className="text-[30px] leading-none font-semibold scheda-font-mono text-[#15161c]">{valore}</span>
        {contorno && <span className="text-xs text-[#9095a1]">{contorno}</span>}
      </div>
    </div>
  );
}

// Contenuto del pannello di destra. Estratto perche' sotto i 1280px lo stesso
// identico contenuto viene mostrato dentro un dialog invece che in colonna.
function DettaglioLead({ lead, onPatch, salvataggio, onClose }) {
  const [nota, setNota] = useState(lead?.notes || "");
  useEffect(() => { setNota(lead?.notes || ""); }, [lead?.lead_id, lead?.notes]);

  if (!lead) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-8 scheda-font-ui" data-testid="crm-detail-empty">
        <Inbox className="w-7 h-7 mb-2 text-[#9095a1]" />
        <div className="text-sm font-medium text-[#15161c]">Nessun lead selezionato</div>
        <div className="text-xs mt-1 text-[#9095a1]">
          Scegli una riga per vederne le risposte e segnarne l'esito.
        </div>
      </div>
    );
  }

  const { nome, email, telefono } = recapiti(lead);
  const { titolo } = identificativo(lead);
  const wa = numeroWhatsApp(telefono);

  // La timeline parte sempre dall'origine del lead, ricostruita da
  // `created_time`: i lead importati prima dell'introduzione della cronologia
  // non hanno l'evento salvato, e senza questo mostrerebbero una lista vuota.
  const attivita = [
    { tipo: "import", testo: "Lead importato da Meta", quando: lead.created_time },
    ...(lead.attivita || []).filter((a) => a.tipo !== "import"),
  ];

  return (
    <div className="h-full flex flex-col scheda-font-ui">
      <div className="px-5 py-4 border-b border-[#e9e9f1]">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-[#15161c]">Dettaglio lead</span>
          <div className="flex items-center gap-2">
            <span className="scheda-font-mono text-[10px] text-[#9095a1]">#{String(lead.lead_id).slice(-6)}</span>
            {onClose && (
              <button
                onClick={onClose}
                data-testid="crm-detail-close-btn"
                className="transition-colors duration-150 hover:text-[#15161c] text-[#9095a1]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3.5 mt-3">
          <div className="w-[46px] h-[46px] rounded-[12px] grid place-items-center text-sm font-semibold shrink-0 bg-[#f1f1f8] text-[#15161c]">
            {iniziali(lead)}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold truncate text-[#15161c]">{nome || titolo}</div>
            <div className="text-xs truncate text-[#9095a1]">{lead.form_name || "-"}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3.5">
          <button
            type="button"
            className={`${PRIMARY_BTN} flex-1 disabled:opacity-50`}
            disabled={!telefono}
            onClick={() => { if (telefono) window.location.href = `tel:${telefono.replace(/\s/g, "")}`; }}
            data-testid="crm-call-btn"
          >
            <Phone className="w-3.5 h-3.5" /> Chiama
          </button>
          <button
            type="button"
            className={`${SECONDARY_BTN} flex-1 justify-center h-9`}
            disabled={!wa}
            onClick={() => window.open(`https://wa.me/${wa}`, "_blank", "noopener")}
            data-testid="crm-whatsapp-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <button
            type="button"
            className={`${ICON_BTN} h-9`}
            disabled={!email}
            title={email ? `Scrivi a ${email}` : "Nessuna email nel modulo"}
            onClick={() => { if (email) window.location.href = `mailto:${email}`; }}
            data-testid="crm-email-btn"
          >
            <Mail className="w-3.5 h-3.5" />
          </button>
        </div>
        {!telefono && (
          <div className="text-[11px] mt-2 text-[#9095a1]">
            Nessun telefono in questo modulo: chiamata e WhatsApp non disponibili.
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider mb-2 text-[#9095a1]">Esito contatto</div>
          <div className="flex flex-wrap gap-1.5" data-testid="crm-status-picker">
            {STATI.map((s) => {
              const attivo = (lead.status || "nuovo") === s.key;
              return (
                <button
                  key={s.key}
                  disabled={salvataggio}
                  onClick={() => onPatch({ status: s.key })}
                  data-testid={`crm-status-${s.key}`}
                  className={`rounded-[10px] px-2.5 py-1.5 text-[11px] font-medium transition-all duration-150 disabled:opacity-50 inline-flex items-center gap-1.5 border ${attivo ? "text-[#15161c]" : "text-[#9095a1] border-[#e4e4ed] bg-transparent"}`}
                  style={attivo ? { background: s.bg, borderColor: s.dot } : undefined}
                >
                  {attivo && <Check className="w-3 h-3" />}
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider mb-2 text-[#9095a1]">Risposte al modulo</div>
          <div className="flex flex-col gap-3">
            {Object.entries(lead.fields || {}).length === 0 && (
              <div className="text-xs italic text-[#9095a1]">Nessun campo in questo modulo</div>
            )}
            {Object.entries(lead.fields || {}).map(([k, v]) => (
              <div key={k}>
                <div className="text-[11px] text-[#9095a1]">{k}</div>
                <div className="text-[13px] break-words text-[#15161c]">{String(v)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider mb-2 text-[#9095a1]">Attività</div>
          <div className="flex flex-col gap-3.5" data-testid="crm-activity">
            {attivita.map((a, i) => (
              <div key={i} className="flex gap-2.5">
                <span className={`w-[7px] h-[7px] rounded-full mt-1.5 shrink-0 ${i === 0 ? "bg-[#5b5bd6]" : "bg-[#dcdce8]"}`} />
                <div className="min-w-0">
                  <div className="text-[13px] text-[#15161c]">{a.testo}</div>
                  <div className="text-[11px] text-[#9095a1]">
                    {formatDateTime(a.quando)}{a.autore ? ` · ${a.autore}` : ""}
                  </div>
                </div>
              </div>
            ))}
            {attivita.length === 1 && (
              <div className="flex gap-2.5">
                <span className="w-[7px] h-[7px] rounded-full mt-1.5 shrink-0 bg-[#dcdce8]" />
                <div>
                  <div className="text-[13px] text-[#15161c]">Nessun contatto ancora</div>
                  <div className="text-[11px] text-[#9095a1]">Aggiungi una nota dopo la chiamata</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider mb-2 text-[#9095a1]">Nota</div>
          <Textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Come è andata la chiamata?"
            className="text-[13px] min-h-[72px] rounded-[10px] border-[#e4e4ed] bg-white shadow-none focus-visible:ring-0 focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)] scheda-font-ui"
            data-testid="crm-note-input"
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              className={`${PRIMARY_BTN} h-9 disabled:opacity-50`}
              disabled={salvataggio || nota === (lead.notes || "")}
              onClick={() => onPatch({ notes: nota })}
              data-testid="crm-note-save"
            >
              {salvataggio ? "Salvo…" : "Salva nota"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Vero da 1280px in su, dove il dettaglio sta nella colonna fissa. Serve in JS
// e non solo in CSS: il Dialog per schermi stretti va proprio non aperto,
// perche' il suo overlay e' un nodo separato che una classe `xl:hidden` sul
// contenuto non tocca — e oscurerebbe la pagina anche su desktop.
function useSchermoLargo() {
  const [largo, setLargo] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const onChange = (e) => setLargo(e.matches);
    mq.addEventListener("change", onChange);
    setLargo(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return largo;
}

export default function CrmPage({ user }) {
  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;
  const isAMM = user?.role === "amm" || isDirettore || (user?.secondary_roles || []).includes("amm") || !!user?.is_admin;
  const canImport = isPM || isAMM;

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [period, setPeriod] = useState("");
  const [formName, setFormName] = useState("");
  const [onlyOrganic, setOnlyOrganic] = useState(false);
  const [statoFiltro, setStatoFiltro] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);

  const [stats, setStats] = useState({
    per_form: [], per_cliente: [], serie_14_giorni: [], per_stato: {},
    ultimo_import: null, totali: 0, archivio: 0, organici: 0,
    non_attribuiti: 0, clienti_distinti: 0, moduli_distinti: 0, nuovi_oggi: 0, nuovi_ieri: 0,
  });
  const [selezionato, setSelezionato] = useState(null);
  const [dettaglioMobile, setDettaglioMobile] = useState(false);

  // Colonna dettaglio animata: un `useState` che semplicemente rispecchia
  // `selezionato` non basta perche' su `null` React la smonta all'istante,
  // senza lasciare il tempo alla transizione CSS di chiudersi. Il contenuto
  // resta montato ("pannelloLead") fino a fine transizione, mentre "aperto"
  // guida la larghezza/opacita'. Al cambio di lead riparte da chiuso e si
  // apre al frame successivo (altrimenti monterebbe gia' aperto, senza
  // animazione d'ingresso); se e' lo stesso lead (es. dopo il salvataggio di
  // una nota) resta aperto senza sfarfallare.
  const [pannelloLead, setPannelloLead] = useState(null);
  const [pannelloAperto, setPannelloAperto] = useState(false);
  const leadIdApertoRef = useRef(null);
  useEffect(() => {
    if (selezionato) {
      const stessoLead = leadIdApertoRef.current === selezionato.lead_id;
      setPannelloLead(selezionato);
      leadIdApertoRef.current = selezionato.lead_id;
      if (stessoLead) { setPannelloAperto(true); return undefined; }
      setPannelloAperto(false);
      const raf = requestAnimationFrame(() => setPannelloAperto(true));
      return () => cancelAnimationFrame(raf);
    }
    leadIdApertoRef.current = null;
    setPannelloAperto(false);
    const t = setTimeout(() => setPannelloLead(null), 300);
    return () => clearTimeout(t);
  }, [selezionato]);
  const schermoLargo = useSchermoLargo();
  const [salvataggio, setSalvataggio] = useState(false);
  const [importing, setImporting] = useState(false);

  const { from, to } = useMemo(() => computePeriodDates(period), [period]);
  const countsByClient = useMemo(() => {
    const map = {};
    for (const c of stats.per_cliente || []) {
      if (c.client_id) map[c.client_id] = c.count;
    }
    return map;
  }, [stats.per_cliente]);

  const paramsFiltri = useCallback(() => {
    const p = new URLSearchParams();
    if (clientId) p.set("client_id", clientId);
    if (from && to) { p.set("from_date", from); p.set("to_date", to); }
    if (formName) p.set("form_name", formName);
    if (onlyOrganic) p.set("is_organic", "true");
    if (statoFiltro) p.set("status", statoFiltro);
    if (search.trim()) p.set("search", search.trim());
    return p;
  }, [clientId, from, to, formName, onlyOrganic, statoFiltro, search]);

  useEffect(() => {
    apiFetch("/clients").then(async (r) => { if (r.ok) setClients(await r.json()); }).catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const r = await apiFetch(`/crm/stats?${paramsFiltri().toString()}`);
      if (r.ok) setStats(await r.json());
    } catch (_) { /* i numeri in testata non bloccano la lista */ }
  }, [paramsFiltri]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      const p = paramsFiltri();
      p.set("skip", String(skip));
      p.set("limit", String(PAGE_SIZE));
      const r = await apiFetch(`/crm/leads?${p.toString()}`);
      if (!r.ok) { setErrore("Errore di rete durante la richiesta. I filtri restano impostati."); return; }
      const data = await r.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (_) {
      setErrore("Errore di rete durante la richiesta. I filtri restano impostati.");
    } finally {
      setLoading(false);
    }
  }, [paramsFiltri, skip]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { setSkip(0); }, [clientId, from, to, formName, onlyOrganic, statoFiltro, search]);

  // Il lead aperto puo' non essere piu' nella pagina dopo un cambio filtro:
  // lasciarlo selezionato mostrerebbe un dettaglio scollegato dalla lista.
  useEffect(() => {
    if (selezionato && !leads.some((l) => l.lead_id === selezionato.lead_id)) setSelezionato(null);
  }, [leads, selezionato]);

  const patchLead = async (patch) => {
    if (!selezionato) return;
    setSalvataggio(true);
    try {
      const r = await apiFetch(`/crm/leads/${selezionato.lead_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || "Salvataggio non riuscito"); return; }
      setSelezionato(data);
      setLeads((prev) => prev.map((l) => (l.lead_id === data.lead_id ? data : l)));
      fetchStats();
      toast.success(patch.status ? "Esito aggiornato" : "Nota salvata");
    } catch (_) {
      toast.error("Errore di rete durante il salvataggio");
    } finally {
      setSalvataggio(false);
    }
  };

  const triggerImport = async () => {
    setImporting(true);
    try {
      const r = await apiFetch("/crm/import", { method: "POST" });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || "Import fallito"); return; }
      toast.success(`Import completato: ${data.nuovi} nuovi, ${data.aggiornati} aggiornati`);
      fetchStats(); fetchLeads();
    } catch (_) {
      toast.error("Errore di rete durante l'import");
    } finally {
      setImporting(false);
    }
  };

  const exportFiltro = async () => {
    try {
      const r = await apiFetch(`/crm/leads-csv?${paramsFiltri().toString()}`);
      if (!r.ok) { toast.error("Export non riuscito"); return; }
      const righe = r.headers.get("X-Righe-Esportate");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `crm_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(righe ? `${righe} lead esportati` : "Export completato");
    } catch (_) {
      toast.error("Errore di rete durante l'export");
    }
  };

  // Su schermo largo il dettaglio e' gia' in colonna: aprire anche il dialog
  // significherebbe coprire la lista con una copia di cio' che si vede gia'.
  const apriLead = (l) => { setSelezionato(l); if (!schermoLargo) setDettaglioMobile(true); };

  const azzeraFiltri = () => {
    setClientId(""); setFormName(""); setOnlyOrganic(false); setStatoFiltro("");
    setPeriod(""); setSearchInput(""); setSearch("");
  };

  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const archivioVuoto = !loading && !errore && stats.archivio === 0;
  const delta = stats.nuovi_ieri
    ? Math.round(((stats.nuovi_oggi - stats.nuovi_ieri) / stats.nuovi_ieri) * 100)
    : null;
  const minutiDaImport = stats.ultimo_import
    ? Math.max(0, Math.round((Date.now() - new Date(stats.ultimo_import).getTime()) / 60000))
    : null;

  const chip = (attivo, label, onToggle, testid) => (
    <button
      key={label}
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-[10px] px-3 h-9 text-[13px] font-medium scheda-font-ui transition-all duration-150 cursor-pointer hover:border-[#c9c9f0] ${attivo ? "bg-[#eeeefc] text-[#5b5bd6] border border-[#c9c9f0]" : "bg-white text-[#565d6d] border border-[#e4e4ed]"}`}
    >
      {label}
      {attivo && <X className="w-3.5 h-3.5 text-[#5b5bd6]" />}
    </button>
  );

  return (
    <div className="space-y-[18px] animate-fade-in scheda-font-ui" data-testid="crm-page">
      {/* Testata + corpo: un unico contenitore bianco con la testata radicata
          in alto e il corpo (filtri + lista + dettaglio) sotto, come un'unica
          card di lavoro — non una SectionCard qualunque perche' la testata
          porta tab e cliente picker, non solo un titolo. */}
      <div className="bg-white border border-[#e9e9f1] rounded-t-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-5 px-4 py-3 flex-wrap">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-bold text-[#15161c]">CRM</span>
            <span className="text-[15px] text-[#9095a1]">Lead</span>
          </div>
          <div className="w-px h-5 hidden sm:block bg-[#e9e9f1]" />
          <div className="flex items-center gap-1">
            {["Lead", "Moduli", "Import", "Report"].map((tab) => {
              const attiva = tab === "Lead";
              return (
                <span
                  key={tab}
                  title={attiva ? undefined : "Sezione non ancora disponibile"}
                  className={`rounded-[8px] px-3 py-1.5 text-[13px] transition-colors duration-150 ${attiva ? "font-medium bg-[#f1f1f8] text-[#15161c]" : "cursor-not-allowed text-[#c3c6d0]"}`}
                  data-testid={`crm-tab-${tab.toLowerCase()}`}
                >
                  {tab}
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-3.5 ml-auto">
            <span className="hidden sm:flex items-center gap-2 text-xs text-[#9095a1]">
              <span className="w-[7px] h-[7px] rounded-full bg-[#12a05c]" />
              {minutiDaImport == null
                ? "Nessun import ancora"
                : minutiDaImport < 60
                  ? `Import di ${minutiDaImport} minuti fa`
                  : `Ultimo import ${formatDateTime(stats.ultimo_import)}`}
            </span>
            {canImport && (
              <button
                type="button"
                className={`${SECONDARY_BTN} h-9`}
                onClick={triggerImport} disabled={importing}
                data-testid="crm-import-now-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${importing ? "animate-spin" : ""}`} />
                {importing ? "Importo…" : "Importa ora"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Corpo: lista + dettaglio in colonna. La colonna di destra esiste solo
          quando c'e' un lead aperto: senza selezione la lista prende tutta la
          larghezza invece di lasciare 400px vuoti con lo stato "nessun lead".
          Flex (non grid) perche' la larghezza della colonna dettaglio deve
          potersi animare in transizione CSS. */}
      <div className="-mt-[18px] flex flex-col xl:flex-row border-x border-b border-[#e9e9f1] rounded-b-[18px] overflow-hidden bg-white">
        <div className="min-w-0 flex-1 p-5 space-y-5 pt-[23px]">
          {/* Cliente + metriche */}
          <div className="flex items-start gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-wider mb-1 text-[#9095a1]">Cliente</div>
              <CrmClientPicker
                clients={clients}
                value={clientId}
                onChange={setClientId}
                countsByClient={countsByClient}
                totalCount={stats.archivio || stats.totali || total}
                testid="crm-client-picker"
              />
            </div>
            <div className="flex items-start gap-8 flex-wrap ml-auto">
              <Metrica
                label="Nuovi oggi"
                valore={stats.nuovi_oggi ?? 0}
                contorno={delta == null ? null : `${delta >= 0 ? "+" : ""}${delta}%`}
                testid="crm-kpi-nuovi"
              />
              <Metrica
                label="Da richiamare"
                valore={stats.per_stato?.da_richiamare ?? 0}
                testid="crm-kpi-richiamare"
              />
              <Metrica
                label="Organici"
                valore={stats.organici ?? 0}
                contorno={`/ ${total.toLocaleString("it-IT")}`}
                testid="crm-kpi-organic"
              />
              <div className="hidden md:block">
                <div className="text-[11px] uppercase tracking-wider mb-1.5 text-[#9095a1]">Ultimi 14 giorni</div>
                <Sparkline serie={stats.serie_14_giorni || []} />
              </div>
            </div>
          </div>

          {/* Filtri */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center gap-2.5 rounded-[10px] px-3 h-9 flex-1 min-w-[220px] bg-white border border-[#e4e4ed]">
              <Search className="w-3.5 h-3.5 shrink-0 text-[#9095a1]" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
                placeholder="Cerca nome, email, telefono…"
                className="border-0 shadow-none h-8 px-0 focus-visible:ring-0 text-[13px] bg-transparent scheda-font-ui"
                data-testid="crm-search-input"
              />
              <button onClick={() => setSearch(searchInput)} data-testid="crm-search-btn" className="text-[#9095a1] hover:text-[#5b5bd6] transition-colors">
                <Search className="w-3.5 h-3.5" />
              </button>
            </div>

            <Select value={formName || "__all__"} onValueChange={(v) => setFormName(v === "__all__" ? "" : v)}>
              <SelectTrigger
                className={`h-9 w-auto min-w-[160px] max-w-[240px] rounded-[10px] text-[13px] font-medium scheda-font-ui transition-all hover:border-[#c9c9f0] focus:ring-0 bg-white ${formName ? "border-[#5b5bd6] text-[#15161c]" : "border-[#e4e4ed] text-[#565d6d]"}`}
                data-testid="crm-form-filter"
              >
                <SelectValue placeholder="Tutti i moduli" />
              </SelectTrigger>
              <SelectContent
                className="rounded-[14px] p-1.5 shadow-lg border border-[#e9e9f1] max-h-[300px] min-w-[240px] z-50 scheda-pop-anim scheda-font-ui bg-white text-[#15161c]"
              >
                <SelectItem
                  value="__all__"
                  className="rounded-[10px] py-2 px-2.5 text-[12.5px] hover:bg-[#f3f3f8] focus:bg-[#f3f3f8] transition-colors cursor-pointer"
                >
                  <span className="font-medium">Tutti i moduli</span>
                </SelectItem>
                {(stats.per_form || []).map((f) => (
                  <SelectItem
                    key={f.form_name || "senza-nome"}
                    value={f.form_name || "senza-nome"}
                    className="rounded-[10px] py-2 px-2.5 text-[12.5px] hover:bg-[#f3f3f8] focus:bg-[#f3f3f8] transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full gap-2.5">
                      <span className="truncate max-w-[170px]">{f.form_name || "Senza nome"}</span>
                      <span className="text-[10.5px] scheda-font-mono px-1.5 py-0.5 rounded-full shrink-0 ml-auto bg-[#f3f3f8] text-[#9095a1]">
                        {f.count}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statoFiltro || "__all__"} onValueChange={(v) => setStatoFiltro(v === "__all__" ? "" : v)}>
              <SelectTrigger
                className={`h-9 w-auto min-w-[150px] rounded-[10px] text-[13px] font-medium scheda-font-ui transition-all hover:border-[#c9c9f0] focus:ring-0 bg-white ${statoFiltro ? "border-[#5b5bd6] text-[#15161c]" : "border-[#e4e4ed] text-[#565d6d]"}`}
                data-testid="crm-status-filter"
              >
                <SelectValue placeholder="Tutti gli esiti" />
              </SelectTrigger>
              <SelectContent
                className="rounded-[14px] p-1.5 shadow-lg border border-[#e9e9f1] max-h-[300px] min-w-[210px] z-50 scheda-pop-anim scheda-font-ui bg-white text-[#15161c]"
              >
                <SelectItem
                  value="__all__"
                  className="rounded-[10px] py-2 px-2.5 text-[12.5px] hover:bg-[#f3f3f8] focus:bg-[#f3f3f8] transition-colors cursor-pointer"
                >
                  <span className="font-medium">Tutti gli esiti</span>
                </SelectItem>
                {STATI.map((s) => (
                  <SelectItem
                    key={s.key}
                    value={s.key}
                    className="rounded-[10px] py-2 px-2.5 text-[12.5px] hover:bg-[#f3f3f8] focus:bg-[#f3f3f8] transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full gap-2.5">
                      <span className="flex items-center gap-2 truncate">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.dot }} />
                        <span>{s.label}</span>
                      </span>
                      <span className="text-[10.5px] scheda-font-mono px-1.5 py-0.5 rounded-full shrink-0 ml-auto bg-[#f3f3f8] text-[#9095a1]">
                        {stats.per_stato?.[s.key] ?? 0}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <PeriodPicker
              value={period}
              onChange={setPeriod}
              allowAll={true}
              testid="crm-period-picker"
            />

            {chip(onlyOrganic, "Solo organici", () => setOnlyOrganic((v) => !v), "crm-organic-toggle")}

            <button
              type="button"
              className={`${SECONDARY_BTN} h-9 ml-auto disabled:opacity-50`}
              onClick={exportFiltro} disabled={!leads.length}
              data-testid="crm-export-csv-btn"
            >
              <FileDown className="w-3.5 h-3.5" /> Esporta CSV
            </button>
          </div>

          {/* Intestazione lista */}
          <div className="flex items-baseline justify-between gap-3 pt-1">
            <div className="text-[13px] text-[#565d6d]">
              <span className="font-semibold scheda-font-mono text-[#15161c]">{total.toLocaleString("it-IT")}</span>
              {" "}lead · ordinati per data
            </div>
            <div className="text-[11px] hidden sm:block text-[#9095a1]">
              Clicca una riga per vedere tutte le risposte
            </div>
          </div>

          {/* Lista */}
          {loading ? (
            <div className="space-y-2" data-testid="crm-loading">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-[10px]" />)}
            </div>
          ) : errore ? (
            <div className="py-14 text-center" data-testid="crm-error">
              <AlertTriangle className="w-7 h-7 mx-auto mb-2 text-[#c0392b]" />
              <div className="text-sm font-semibold text-[#15161c]">Non è stato possibile caricare i lead</div>
              <div className="text-xs mt-1 text-[#9095a1]">{errore}</div>
              <button type="button" className={`${SECONDARY_BTN} h-8 mt-3 mx-auto`} onClick={fetchLeads} data-testid="crm-retry-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Riprova
              </button>
            </div>
          ) : archivioVuoto ? (
            <div className="py-14 text-center" data-testid="crm-empty-archive">
              <Inbox className="w-7 h-7 mx-auto mb-2 text-[#9095a1]" />
              <div className="text-sm font-semibold text-[#15161c]">Nessun import ancora eseguito</div>
              <div className="text-xs mt-1 max-w-md mx-auto text-[#9095a1]">
                I lead dei moduli Facebook e Instagram compaiono qui dopo il primo import.
                L'import automatico gira ogni ora.
              </div>
            </div>
          ) : leads.length === 0 ? (
            <div className="py-14 text-center" data-testid="crm-empty">
              <Search className="w-7 h-7 mx-auto mb-2 text-[#9095a1]" />
              <div className="text-sm font-semibold text-[#15161c]">Nessun lead con questi filtri</div>
              <EmptyState>{`su ${stats.archivio.toLocaleString("it-IT")} lead in archivio`}</EmptyState>
              <button type="button" className={`${SECONDARY_BTN} h-8 mt-3 mx-auto`} onClick={azzeraFiltri} data-testid="crm-empty-clear">
                Azzera i filtri
              </button>
            </div>
          ) : (
            <div data-testid="crm-leads-table">
              {leads.map((l, i) => {
                const { data, ora } = splitDateTime(l.created_time);
                const { titolo, sotto, assente } = identificativo(l);
                const attivo = selezionato?.lead_id === l.lead_id;
                return (
                  <div
                    key={l.lead_id}
                    onClick={() => apriLead(l)}
                    data-testid={`crm-lead-row-${i}`}
                    className={`grid grid-cols-[92px_1fr_auto] md:grid-cols-[116px_1fr_190px_132px_30px] gap-4 md:gap-[18px] items-center px-3 py-3 cursor-pointer rounded-[10px] transition-colors duration-150 border-b border-[#f0f0f6] ${attivo ? "bg-[#f1f1f8]" : ""}`}
                  >
                    <div>
                      <div className="text-[13px] scheda-font-mono text-[#15161c]">{data}</div>
                      <div className="text-[11px] scheda-font-mono text-[#9095a1]">{ora}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5">
                        <span className={`text-[14px] truncate ${assente ? "italic text-[#9095a1]" : "font-medium text-[#15161c]"}`}>
                          {titolo}
                        </span>
                        {l.is_organic && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-[6px] shrink-0 bg-[#e7f7ee] text-[#0f7040]">
                            organico
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] truncate text-[#565d6d]">{sotto}</div>
                      <div className="text-[11px] truncate md:hidden mt-0.5 text-[#9095a1]">
                        {l.client_name || "Non attribuito"} · {l.form_name || "-"}
                      </div>
                    </div>
                    <div className="hidden md:block min-w-0">
                      <div className="text-[12px] truncate text-[#3c414d]">{l.form_name || "-"}</div>
                      <div className="text-[11px] truncate text-[#9095a1]">
                        {l.client_name || "Non attribuito"}
                      </div>
                    </div>
                    <div className="hidden md:block"><PillStato stato={l.status} /></div>
                    <div className="flex justify-end md:justify-center">
                      <ChevronRight className="w-4 h-4 text-[#9095a1]" />
                    </div>
                  </div>
                );
              })}

              {/* Paginazione */}
              <div className="flex items-center justify-between pt-4 text-[12px] text-[#9095a1]">
                <span>Pagina {page} di {pageCount} · {PAGE_SIZE} per pagina</span>
                <div className="flex items-center gap-1.5">
                  <button type="button" className={ICON_BTN}
                    onClick={() => setSkip(0)} disabled={skip === 0} data-testid="crm-page-first">
                    <ChevronsLeft className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" className={ICON_BTN}
                    onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))} disabled={skip === 0} data-testid="crm-page-prev">
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" className={ICON_BTN}
                    onClick={() => setSkip((s) => s + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total} data-testid="crm-page-next">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Colonna dettaglio: da 1280px in su, solo quando un lead e' aperto.
            Larghezza e opacita' animate; il contenuto interno resta a 400px
            fissi cosi' non si "schiaccia" mentre la colonna si apre/chiude. */}
        {pannelloLead && (
          <div
            className="hidden xl:block border-l border-[#e9e9f1] overflow-hidden bg-white"
            style={{
              width: pannelloAperto ? 400 : 0,
              opacity: pannelloAperto ? 1 : 0,
              transition: "width 300ms ease-in-out, opacity 220ms ease-in-out",
            }}
            data-testid="crm-detail-panel"
          >
            <div className="w-[400px] h-full">
              <DettaglioLead
                lead={pannelloLead}
                onPatch={patchLead}
                salvataggio={salvataggio}
                onClose={() => setSelezionato(null)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Sotto i 1280px lo stesso pannello diventa un dialog */}
      <Dialog open={!schermoLargo && dettaglioMobile && !!selezionato} onOpenChange={(v) => setDettaglioMobile(v)}>
        <DialogContent className="xl:hidden max-w-lg p-0 gap-0 max-h-[85vh] rounded-[18px] border-[#e9e9f1] scheda-font-ui" data-testid="crm-lead-detail-dialog">
          <div className="max-h-[85vh] overflow-hidden rounded-[18px] bg-white">
            <DettaglioLead
              lead={selezionato}
              onPatch={patchLead}
              salvataggio={salvataggio}
              onClose={() => setDettaglioMobile(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
